import React, { useState } from 'react';
import { api } from '../../../services/api';
import { useToast } from '../../../components/ui/use-toast';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { 
  X, 
  Save, 
  Building, 
  User,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Code,
  Plus,
  CreditCard,
  Tag
} from 'lucide-react';

const NewOrganizationModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    type: '',
    contact_person: '',
    contact_phone: '',
    contact_email: '',
    address: '',
    tax_number: '',
    contract_start_date: '',
    contract_end_date: ''
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const { toast } = useToast();

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Kurum adı zorunludur';
    }
    
    if (!formData.code.trim()) {
      newErrors.code = 'Kurum kodu zorunludur';
    }

    if (formData.contact_email && !formData.contact_email.includes('@')) {
      newErrors.contact_email = 'Geçerli bir e-posta adresi giriniz';
    }

    if (formData.contract_start_date && formData.contract_end_date) {
      if (new Date(formData.contract_start_date) >= new Date(formData.contract_end_date)) {
        newErrors.contract_end_date = 'Bitiş tarihi başlangıç tarihinden sonra olmalıdır';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      
      // API endpoint'ine POST request gönder
      const response = await api.post('/pricing/organizations', formData);
      
      if (response.data.success) {
        toast({
          title: "Başarılı",
          description: "Yeni kurum başarıyla eklendi",
          duration: 3000
        });
        
        // Reset form
        setFormData({
          name: '',
          code: '',
          type: '',
          contact_person: '',
          contact_phone: '',
          contact_email: '',
          address: '',
          tax_number: '',
          contract_start_date: '',
          contract_end_date: ''
        });
        
        if (onSuccess) {
          onSuccess(); // Refresh the organizations list
        }
        onClose(); // Close modal
      }
    } catch (error) {
      console.error('Error creating organization:', error);
      toast({
        variant: "destructive",
        title: "Hata",
        description: error.response?.data?.message || "Kurum eklenirken hata oluştu",
        duration: 5000
      });
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      code: '',
      type: '',
      contact_person: '',
      contact_phone: '',
      contact_email: '',
      address: '',
      tax_number: '',
      contract_start_date: '',
      contract_end_date: ''
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white/95 backdrop-blur-xl rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-white/20">
        {/* Modern Header */}
        <div className="sticky top-0 z-50 bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 rounded-t-3xl px-8 py-6 backdrop-blur-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-2xl">
                <Plus className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">
                  Yeni Kurum Ekle
                </h2>
                <p className="text-white/80">
                  Yeni bir anlaşmalı kurum ekleyin
                </p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleClose}
              className="bg-white/10 hover:bg-white/20 text-white rounded-2xl p-3"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        <div className="p-8 relative z-10">
          <div className="space-y-8">
            {/* Basic Information */}
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <div className="p-3 bg-blue-100 rounded-2xl">
                  <Building className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Temel Bilgiler</h3>
                  <p className="text-gray-600">Kurum adı, kodu ve türü bilgileri</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <div className="flex items-center space-x-2 mb-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Building className="w-4 h-4 text-blue-600" />
                    </div>
                    <Label htmlFor="name" className="font-medium text-gray-700">
                      Kurum Adı *
                    </Label>
                  </div>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Örn: Arnavutköy Belediyesi"
                    className={`rounded-2xl border-0 bg-blue-50 focus:ring-2 focus:ring-blue-500 text-lg ${
                      errors.name ? 'ring-2 ring-red-500' : ''
                    }`}
                  />
                  {errors.name && (
                    <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                  )}
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center space-x-2 mb-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Code className="w-4 h-4 text-purple-600" />
                    </div>
                    <Label htmlFor="code" className="font-medium text-gray-700">
                      Kurum Kodu *
                    </Label>
                  </div>
                  <Input
                    id="code"
                    value={formData.code}
                    onChange={(e) => handleInputChange('code', e.target.value.toUpperCase())}
                    placeholder="Örn: ARNAVUTKOY_BEL"
                    className={`rounded-2xl border-0 bg-purple-50 focus:ring-2 focus:ring-purple-500 text-lg ${
                      errors.code ? 'ring-2 ring-red-500' : ''
                    }`}
                  />
                  {errors.code && (
                    <p className="text-red-500 text-sm mt-1">{errors.code}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <div className="flex items-center space-x-2 mb-3">
                    <div className="p-2 bg-teal-100 rounded-lg">
                      <Tag className="w-4 h-4 text-teal-600" />
                    </div>
                    <Label htmlFor="type" className="font-medium text-gray-700">
                      Kurum Türü
                    </Label>
                  </div>
                  <Select 
                    value={formData.type} 
                    onValueChange={(value) => handleInputChange('type', value)}
                  >
                    <SelectTrigger className="rounded-2xl border-0 bg-teal-50 focus:ring-2 focus:ring-teal-500 text-lg">
                      <SelectValue placeholder="Kurum türünü seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hospital">Hastane</SelectItem>
                      <SelectItem value="government">Devlet Kurumu</SelectItem>
                      <SelectItem value="private">Özel Şirket</SelectItem>
                      <SelectItem value="municipality">Belediye</SelectItem>
                      <SelectItem value="foundation">Vakıf/Dernek</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center space-x-2 mb-3">
                    <div className="p-2 bg-amber-100 rounded-lg">
                      <CreditCard className="w-4 h-4 text-amber-600" />
                    </div>
                    <Label htmlFor="tax_number" className="font-medium text-gray-700">
                      Vergi Numarası
                    </Label>
                  </div>
                  <Input
                    id="tax_number"
                    value={formData.tax_number}
                    onChange={(e) => handleInputChange('tax_number', e.target.value)}
                    placeholder="1234567890"
                    className="rounded-2xl border-0 bg-amber-50 focus:ring-2 focus:ring-amber-500 text-lg"
                  />
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <div className="p-3 bg-green-100 rounded-2xl">
                  <User className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">İletişim Bilgileri</h3>
                  <p className="text-gray-600">Yetkili kişi ve iletişim detayları</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <div className="flex items-center space-x-2 mb-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <User className="w-4 h-4 text-green-600" />
                    </div>
                    <Label htmlFor="contact_person" className="font-medium text-gray-700">
                      Yetkili Kişi
                    </Label>
                  </div>
                  <Input
                    id="contact_person"
                    value={formData.contact_person}
                    onChange={(e) => handleInputChange('contact_person', e.target.value)}
                    placeholder="Yetkili kişi adı"
                    className="rounded-2xl border-0 bg-green-50 focus:ring-2 focus:ring-green-500 text-lg"
                  />
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center space-x-2 mb-3">
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <Phone className="w-4 h-4 text-orange-600" />
                    </div>
                    <Label htmlFor="contact_phone" className="font-medium text-gray-700">
                      Telefon
                    </Label>
                  </div>
                  <Input
                    id="contact_phone"
                    value={formData.contact_phone}
                    onChange={(e) => handleInputChange('contact_phone', e.target.value)}
                    placeholder="0212 555 12 34"
                    className="rounded-2xl border-0 bg-orange-50 focus:ring-2 focus:ring-orange-500 text-lg"
                  />
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center space-x-2 mb-3">
                    <div className="p-2 bg-indigo-100 rounded-lg">
                      <Mail className="w-4 h-4 text-indigo-600" />
                    </div>
                    <Label htmlFor="contact_email" className="font-medium text-gray-700">
                      E-posta
                    </Label>
                  </div>
                  <Input
                    id="contact_email"
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => handleInputChange('contact_email', e.target.value)}
                    placeholder="yetkili@kurum.com"
                    className={`rounded-2xl border-0 bg-indigo-50 focus:ring-2 focus:ring-indigo-500 text-lg ${
                      errors.contact_email ? 'ring-2 ring-red-500' : ''
                    }`}
                  />
                  {errors.contact_email && (
                    <p className="text-red-500 text-sm mt-1">{errors.contact_email}</p>
                  )}
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center space-x-2 mb-3">
                    <div className="p-2 bg-gray-100 rounded-lg">
                      <MapPin className="w-4 h-4 text-gray-600" />
                    </div>
                    <Label htmlFor="address" className="font-medium text-gray-700">
                      Adres
                    </Label>
                  </div>
                  <Textarea
                    id="address"
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    placeholder="Kurum adresi"
                    rows={3}
                    className="rounded-2xl border-0 bg-gray-50 focus:ring-2 focus:ring-gray-500 text-lg resize-none"
                  />
                </div>
              </div>
            </div>

            {/* Contract Information */}
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <div className="p-3 bg-yellow-100 rounded-2xl">
                  <Calendar className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Sözleşme Bilgileri</h3>
                  <p className="text-gray-600">Anlaşma başlangıç ve bitiş tarihleri</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <div className="flex items-center space-x-2 mb-3">
                    <div className="p-2 bg-yellow-100 rounded-lg">
                      <Calendar className="w-4 h-4 text-yellow-600" />
                    </div>
                    <Label htmlFor="contract_start_date" className="font-medium text-gray-700">
                      Başlangıç Tarihi
                    </Label>
                  </div>
                  <Input
                    id="contract_start_date"
                    type="date"
                    value={formData.contract_start_date}
                    onChange={(e) => handleInputChange('contract_start_date', e.target.value)}
                    className="rounded-2xl border-0 bg-yellow-50 focus:ring-2 focus:ring-yellow-500 text-lg"
                  />
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center space-x-2 mb-3">
                    <div className="p-2 bg-red-100 rounded-lg">
                      <Calendar className="w-4 h-4 text-red-600" />
                    </div>
                    <Label htmlFor="contract_end_date" className="font-medium text-gray-700">
                      Bitiş Tarihi
                    </Label>
                  </div>
                  <Input
                    id="contract_end_date"
                    type="date"
                    value={formData.contract_end_date}
                    onChange={(e) => handleInputChange('contract_end_date', e.target.value)}
                    className={`rounded-2xl border-0 bg-red-50 focus:ring-2 focus:ring-red-500 text-lg ${
                      errors.contract_end_date ? 'ring-2 ring-red-500' : ''
                    }`}
                  />
                  {errors.contract_end_date && (
                    <p className="text-red-500 text-sm mt-1">{errors.contract_end_date}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modern Footer */}
        <div className="sticky bottom-0 z-40 bg-white/90 backdrop-blur-xl border-t border-gray-200/50 px-8 py-6 rounded-b-3xl">
          <div className="flex items-center justify-between">
            <Button 
              variant="outline" 
              onClick={handleClose}
              size="lg"
              className="bg-white/60 border-0 hover:bg-white/80 rounded-2xl px-8"
            >
              İptal
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              size="lg"
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 rounded-2xl px-8"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Kaydediliyor...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5 mr-2" />
                  Kaydet
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewOrganizationModal; 